/**
 * Inertia navigation hooks for Algolia routing (Vue/Inertia bundle only).
 * Imported as a side effect from algoliaRouting.ts — never from Blade entrypoints.
 */
import { router as inertiaRouter } from "@inertiajs/vue3";

import {
    clearPendingPopstate,
    hasCuratedPathname,
    peekPendingPopstate,
    setInertiaNavigating,
} from "./algoliaRouting.shared";

const DEBUG_ALGOLIA_ROUTING =
    import.meta.env.VITE_DEVELOPMENT === "true" ||
    (typeof window !== "undefined" &&
        window.location.hostname.startsWith("pp."));

const logNav = (eventName: string, extra?: Record<string, unknown>) => {
    if (!DEBUG_ALGOLIA_ROUTING) return;
    console.log("[Nav]", eventName, ...(extra ? ["|", extra] : []));
};

if (typeof window !== "undefined") {
    let currentPathname = window.location.pathname;
    let navigationFinishTimeout: ReturnType<typeof setTimeout> | null = null;

    // Intercepte la navigation Inertia avant qu'elle ne monte le composant.
    // Si on revient via back/forward sur une page curée, on force une hard navigation
    // pour éviter que l'état Algolia (filtres marketplace) ne se greffe sur la page.
    inertiaRouter.on("before", (event: any) => {
        try {
            const visitUrl = event?.detail?.visit?.url;
            const nextUrl = visitUrl
                ? new URL(String(visitUrl), window.location.origin)
                : null;
            const isCurated =
                !!nextUrl && hasCuratedPathname(nextUrl.pathname);
            logNav("inertia:before", {
                isPendingPopstate: peekPendingPopstate(),
                destination: nextUrl?.pathname,
                isCurated,
            });
            if (peekPendingPopstate() && nextUrl && isCurated) {
                clearPendingPopstate();
                event.preventDefault();
                window.location.href = nextUrl.href;
            } else {
                clearPendingPopstate();
            }
        } catch {
            clearPendingPopstate();
        }
    });

    inertiaRouter.on("start", (event: any) => {
        if (navigationFinishTimeout) {
            clearTimeout(navigationFinishTimeout);
            navigationFinishTimeout = null;
        }
        let nextPathname: string | null = null;
        try {
            const visitUrl = event?.detail?.visit?.url;
            if (visitUrl) {
                const nextUrl = new URL(visitUrl, window.location.origin);
                nextPathname = nextUrl.pathname;
            }
        } catch {
            nextPathname = null;
        }

        // IMPORTANT: ne bloquer routeToState que pour les navigations cross-page.
        // Un changement same-path (query différente sur /marketplace) doit rester
        // géré par le router InstantSearch, sinon on vide les facettes au back.
        setInertiaNavigating(
            !!nextPathname && nextPathname !== currentPathname,
        );
        logNav("inertia:start", {
            destination: nextPathname,
            isInertiaNavigating:
                !!nextPathname && nextPathname !== currentPathname,
        });
    });

    inertiaRouter.on("finish", () => {
        currentPathname = window.location.pathname;
        logNav("inertia:finish");
        navigationFinishTimeout = setTimeout(() => {
            setInertiaNavigating(false);
            navigationFinishTimeout = null;
        }, 150);
    });
}
